import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { BrandedTransitionScreen } from '~/components/ui/BrandedTransitionScreen';
import { ErrorState } from '~/components/ui/ErrorState';
import { YakSplashScreen } from '~/components/ui/YakSplashScreen';
import { apiClient } from '~/lib/api';
import { resolveDomain } from '~/lib/domain';
import type { PublicLandingPage } from '~/types';
import { PublicLandingPageSchema } from '~/types/schemas';

export const Route = createFileRoute('/$slug')({
  beforeLoad: async ({ params, context }) => {
    const { slug: pathSlug } = params;

    // Resolve domain: custom domains use hostname as slug, primary hosts use path
    const { type, value } = resolveDomain(
      window.location.hostname,
      window.location.pathname,
    );

    const slug = type === 'custom_domain' ? value : pathSlug;

    // Fetch and cache landing page data for all child routes
    const landingPage: PublicLandingPage = await context.queryClient.ensureQueryData({
      queryKey: ['landingPage', slug],
      queryFn: async () => {
        const data = await apiClient<unknown>(
          `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
        );
        return PublicLandingPageSchema.parse(data) as PublicLandingPage;
      },
      staleTime: 1000 * 60 * 10, // 10 minutes
    });

    return {
      slug,
      landingPage,
    };
  },
  component: SlugLayout,
  pendingComponent: () => <YakSplashScreen />,
  errorComponent: ({ error, reset }) => (
    <ErrorState
      message={error?.message || 'Failed to load this page. Please try again.'}
      onRetry={reset}
    />
  ),
});

function SlugLayout() {
  const [showTransition, setShowTransition] = useState(true);
  const context = Route.useRouteContext();

  const handleTransitionComplete = useCallback(() => {
    setShowTransition(false);
  }, []);

  // Safety timeout: ensure transition clears even if callback fails
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      setShowTransition(false);
    }, 3000);
    return () => clearTimeout(safetyTimer);
  }, []);

  if (!context.landingPage) {
    return <ErrorState message="Landing page data unavailable" />;
  }

  const { landingPage } = context;

  return (
    <>
      {showTransition ? (
        <BrandedTransitionScreen
          branding={landingPage.branding}
          duration={1000}
          onTransitionComplete={handleTransitionComplete}
        />
      ) : (
        <Outlet />
      )}
    </>
  );
}

// Type augmentation for child route access to context
declare module '@tanstack/react-router' {
  interface RouteContext {
    slug?: string;
    landingPage?: PublicLandingPage;
  }
}
